import type { ReactNode } from 'react';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import noop from 'lodash/noop';
import { usePathname } from 'next/navigation';
import { createContext, useContextSelector } from 'use-context-selector';
import { useDownloadAssetsWithWarning } from '@frameio/assets/operations';
import type { DownloadAssetsType } from '@frameio/shared/assets/types';
import { KeyboardShortcut } from '@frameio/shared/commands';
import {
  useActiveAccountId,
  useActiveProjectId,
} from '@frameio/shared/contexts';
import { getPathnameFromHref } from '@frameio/shared/routing/helpers/getPathnameFromHref';
import { usePrevious } from '@frameio/vapor';
import { useClearAssetClipMatch } from '../../hooks/useAssetClipMatch';
import type { GetQuickSearchResultsV2QueryVariables } from '../../quick-search/gql/generated/GetQuickSearchResultsV2';
import type { FilterKeys, QuickSearchFilters } from '../../quick-search/types';
import { getSearchOpenedSource } from '../../quick-search/utils';
import { LEXICAL_SEARCH_TYPE } from '../constants';
import type { SearchMode, SectionId } from '../types';
import { useTrackSearchOpened } from './SearchAnalyticsContext';

type CloseModalOptions = { clearState?: boolean; navigatingTo?: string };

export type { CloseModalOptions };

export type QuickSearchState = {
  activeTab: SectionId | null;
  clearAllFilters: () => void;
  clearQuery: () => void;
  clearScopedToProject: () => void;
  clearScopedToProjectId: () => void;
  clearSelectedProject: () => void;
  closeModal: (options?: CloseModalOptions) => void;
  downloadAssets: DownloadAssetsType;
  filters: QuickSearchFilters;
  focusedKey: string | null;
  focusedKeyByTab: Partial<Record<SectionId, string | null>>;
  isAssetDownloading: boolean;
  isModalOpen: boolean;
  openModal: () => void;
  query: string;
  requestVariables: GetQuickSearchResultsV2QueryVariables;
  scopedToProject: { id: string; name: string; imageUrl?: string } | null;
  scopedToProjectId: string | null;
  searchMode: SearchMode;
  selectedProject: { projectId: string; workspaceId: string } | null;
  setActiveTab: (tab: SectionId) => void;
  setFocusedKey: (key: string) => void;
  setQuery: (query: string) => void;
  setSearchMode: (searchMode: SearchMode) => void;
  setScopedToProject: (project: {
    id: string;
    name: string;
    imageUrl?: string;
  }) => void;
  setScopedToProjectId: (projectId: string) => void;
  setSelectedProject: (projectId: string, workspaceId: string) => void;
  toggleFilter: (filter: FilterKeys, value: boolean) => void;
};

const DEFAULT_FILTERS: QuickSearchFilters = {
  assets: false,
  folders: false,
  projects: false,
};

export const defaultState: QuickSearchState = {
  activeTab: null,
  clearAllFilters: noop,
  clearQuery: noop,
  clearScopedToProject: noop,
  clearScopedToProjectId: noop,
  clearSelectedProject: noop,
  closeModal: noop as (options?: CloseModalOptions) => void,
  downloadAssets: noop,
  filters: DEFAULT_FILTERS,
  focusedKey: null,
  focusedKeyByTab: {},
  isAssetDownloading: false,
  isModalOpen: false,
  openModal: noop,
  query: '',
  requestVariables: {} as GetQuickSearchResultsV2QueryVariables,
  scopedToProject: null,
  scopedToProjectId: null,
  searchMode: LEXICAL_SEARCH_TYPE,
  selectedProject: null,
  setActiveTab: noop,
  setFocusedKey: noop,
  setQuery: noop,
  setScopedToProject: noop,
  setScopedToProjectId: noop,
  setSearchMode: noop,
  setSelectedProject: noop,
  toggleFilter: noop,
};

export type QuickSearchAction =
  | {
      type: 'SET_QUERY';
      payload: string;
    }
  | {
      type: 'TOGGLE_FILTER';
      payload: { filter: FilterKeys; value: boolean };
    }
  | {
      type: 'CLEAR_FILTERS';
    }
  | {
      type: 'CLEAR_QUERY';
    }
  | {
      type: 'OPEN_MODAL';
    }
  | {
      type: 'CLOSE_MODAL';
    }
  | {
      type: 'SET_SELECTED_PROJECT';
      payload: { projectId: string; workspaceId: string };
    }
  | {
      type: 'CLEAR_SELECTED_PROJECT';
    }
  | {
      type: 'SET_FOCUSED_KEY';
      payload: { key: string };
    }
  | {
      type: 'SET_SCOPED_PROJECT';
      payload: { project: { id: string; name: string; imageUrl?: string } };
    }
  | {
      type: 'CLEAR_SCOPED_PROJECT';
    }
  | {
      type: 'SET_SCOPED_PROJECT_ID';
      payload: { projectId: string };
    }
  | {
      type: 'CLEAR_SCOPED_PROJECT_ID';
    }
  | {
      type: 'SET_ACTIVE_TAB';
      payload: { tab: SectionId };
    }
  | {
      type: 'SET_SEARCH_MODE';
      payload: { searchMode: SearchMode };
    }
  | {
      type: 'RESET_STATE';
    };

export const quickSearchReducer = (
  state: QuickSearchState,
  action: QuickSearchAction
) => {
  if (action.type === 'OPEN_MODAL') {
    return { ...state, isModalOpen: true };
  }

  if (action.type === 'CLOSE_MODAL') {
    return { ...state, isModalOpen: false };
  }

  if (action.type === 'SET_SELECTED_PROJECT') {
    const { projectId, workspaceId } = action.payload;
    return { ...state, selectedProject: { projectId, workspaceId } };
  }

  if (action.type === 'CLEAR_SELECTED_PROJECT') {
    return { ...state, selectedProject: null };
  }

  if (action.type === 'SET_QUERY') {
    return action.payload !== state.query
      ? {
          ...state,
          query: action.payload,
          activeTab: null,
          focusedKeyByTab: {},
        }
      : state;
  }

  if (action.type === 'TOGGLE_FILTER') {
    return {
      ...state,
      filters: {
        ...state.filters,
        [action.payload.filter]: action.payload.value,
      },
    };
  }

  if (action.type === 'CLEAR_FILTERS') {
    return { ...state, filters: DEFAULT_FILTERS };
  }

  if (action.type === 'CLEAR_QUERY') {
    return {
      ...state,
      query: '',
      activeTab: null,
      focusedKeyByTab: {},
    };
  }

  if (action.type === 'SET_FOCUSED_KEY') {
    return { ...state, focusedKey: action.payload.key };
  }

  if (action.type === 'SET_SCOPED_PROJECT') {
    return {
      ...state,
      scopedToProject: action.payload.project,
      scopedToProjectId: action.payload.project.id,
    };
  }

  if (action.type === 'CLEAR_SCOPED_PROJECT') {
    return { ...state, scopedToProject: null, scopedToProjectId: null };
  }

  if (action.type === 'SET_SCOPED_PROJECT_ID') {
    return { ...state, scopedToProjectId: action.payload.projectId };
  }

  if (action.type === 'CLEAR_SCOPED_PROJECT_ID') {
    return { ...state, scopedToProjectId: null };
  }

  if (action.type === 'SET_ACTIVE_TAB') {
    const { tab } = action.payload;
    if (tab === state.activeTab) return state;

    // Only save the departing tab's focusedKey when there was an actual departing tab.
    // When activeTab is null (loading, no selection yet) there is nothing to save.
    // Assign to a local const so TypeScript narrows to SectionId for the computed key.
    const departingTab = state.activeTab;
    const focusedKeyByTab =
      departingTab !== null
        ? { ...state.focusedKeyByTab, [departingTab]: state.focusedKey }
        : { ...state.focusedKeyByTab };

    return {
      ...state,
      activeTab: tab,
      focusedKey: focusedKeyByTab[tab] ?? null,
      focusedKeyByTab,
    };
  }

  if (action.type === 'SET_SEARCH_MODE') {
    const { searchMode } = action.payload;
    if (searchMode === state.searchMode) return state;

    return {
      ...state,
      searchMode,
    };
  }

  if (action.type === 'RESET_STATE') {
    return defaultState;
  }

  return state;
};

/** The number of search results to fetch per page. */
const SEARCH_RESULTS_PAGE_COUNT = 50;

/**
 * A helper function to determine if any search filters are active.
 * If no filters are active, it returns `undefined` to avoid sending an
 * empty `filters` object to the API.
 *
 * @param filters - The current search filters.
 * @returns The active filters or `undefined`.
 */
function getQuickSearchFilters(
  filters: QuickSearchFilters
): QuickSearchFilters | undefined {
  const hasAppliedFilters = Object.values(filters).some(Boolean);
  return hasAppliedFilters ? filters : undefined;
}

export const QuickSearchStateContext = createContext(defaultState);

export const QuickSearchStateContextProvider = memo(
  ({ children }: { children: ReactNode }) => {
    const [state, dispatch] = useReducer(quickSearchReducer, defaultState);
    const openModal = useCallback(() => dispatch({ type: 'OPEN_MODAL' }), []);

    // Hold the query and destination pathname to restore / scope on the next modal
    // open. Set when closing via navigation (no clearState) so the query survives a
    // RESET_STATE triggered by the route change. Cleared on explicit close, on
    // account change, or when the user navigates away from the destination
    // pathname (see pathname-change effect below).
    const preservedQueryRef = useRef<string | null>(null);
    const preservedDestinationPathnameRef = useRef<string | null>(null);
    const latestQueryRef = useRef(state.query);
    latestQueryRef.current = state.query;

    const closeModal = useCallback((options?: CloseModalOptions) => {
      if (options?.clearState) {
        preservedQueryRef.current = null;
        preservedDestinationPathnameRef.current = null;
        dispatch({ type: 'CLEAR_QUERY' });
        dispatch({ type: 'CLEAR_FILTERS' });
        dispatch({
          type: 'SET_SEARCH_MODE',
          payload: { searchMode: LEXICAL_SEARCH_TYPE },
        });
      } else {
        preservedQueryRef.current = latestQueryRef.current || null;
        preservedDestinationPathnameRef.current = options?.navigatingTo
          ? getPathnameFromHref(options.navigatingTo)
          : null;
      }
      dispatch({ type: 'CLOSE_MODAL' });
    }, []);
    const trackSearchOpened = useTrackSearchOpened();
    const clearAssetClipMatch = useClearAssetClipMatch();
    const { downloadAssets, isDownloading } = useDownloadAssetsWithWarning();
    const pathname = usePathname();

    const accountId = useActiveAccountId();
    const previousAccountId = usePrevious(accountId);
    const activeProjectId = useActiveProjectId();
    const previousScopedToProjectId = usePrevious(activeProjectId);

    // Reset to default state when the account id or scoped project id changes
    useEffect(() => {
      const hasChangedAccount =
        previousAccountId && previousAccountId !== accountId;

      const hasChangedScopedProject =
        previousScopedToProjectId &&
        previousScopedToProjectId !== activeProjectId;

      if (hasChangedAccount) {
        preservedQueryRef.current = null;
        preservedDestinationPathnameRef.current = null;
        dispatch({ type: 'RESET_STATE' });
      } else if (hasChangedScopedProject) {
        dispatch({ type: 'RESET_STATE' });
      }
    }, [
      accountId,
      activeProjectId,
      previousAccountId,
      previousScopedToProjectId,
    ]);

    // The last known query is preserved when the user navigates to a result from quick search.
    // When the user navigates away from the destination pathname, we clear the preserved query and filters,
    // ensuring a fresh search experience the next time it's opened.
    useEffect(() => {
      if (
        preservedDestinationPathnameRef.current &&
        preservedDestinationPathnameRef.current !== pathname
      ) {
        preservedQueryRef.current = null;
        preservedDestinationPathnameRef.current = null;
        dispatch({ type: 'CLEAR_QUERY' });
        dispatch({ type: 'CLEAR_FILTERS' });
      }
    }, [pathname]);

    const handleOpenModal = useCallback(() => {
      clearAssetClipMatch();
      if (preservedQueryRef.current) {
        dispatch({ type: 'SET_QUERY', payload: preservedQueryRef.current });
        preservedQueryRef.current = null;
      }
      preservedDestinationPathnameRef.current = null;
      openModal();
    }, [clearAssetClipMatch, openModal]);

    const setSelectedProject = useCallback(
      (projectId: string, workspaceId: string) => {
        dispatch({
          type: 'SET_SELECTED_PROJECT',
          payload: { projectId, workspaceId },
        });
      },
      []
    );
    const clearSelectedProject = useCallback(() => {
      dispatch({ type: 'CLEAR_SELECTED_PROJECT' });
    }, []);

    const setQuery = useCallback(
      (query: string) => {
        dispatch({ type: 'SET_QUERY', payload: query });
      },
      [dispatch]
    );
    const toggleFilter = useCallback(
      (filter: FilterKeys, value: boolean) => {
        dispatch({ type: 'TOGGLE_FILTER', payload: { filter, value } });
      },
      [dispatch]
    );
    const clearAllFilters = useCallback(() => {
      dispatch({ type: 'CLEAR_FILTERS' });
    }, [dispatch]);

    const clearQuery = useCallback(() => {
      dispatch({ type: 'CLEAR_QUERY' });
    }, [dispatch]);

    const setFocusedKey = useCallback(
      (key: string) => {
        dispatch({ type: 'SET_FOCUSED_KEY', payload: { key } });
      },
      [dispatch]
    );

    const setScopedToProject = useCallback(
      (project: { id: string; name: string; imageUrl?: string }) => {
        dispatch({ type: 'SET_SCOPED_PROJECT', payload: { project } });
      },
      [dispatch]
    );

    const clearScopedToProject = useCallback(() => {
      dispatch({ type: 'CLEAR_SCOPED_PROJECT' });
    }, [dispatch]);

    const setScopedToProjectId = useCallback(
      (projectId: string) => {
        dispatch({ type: 'SET_SCOPED_PROJECT_ID', payload: { projectId } });
      },
      [dispatch]
    );

    const clearScopedToProjectId = useCallback(() => {
      dispatch({ type: 'CLEAR_SCOPED_PROJECT_ID' });
    }, [dispatch]);

    const setActiveTab = useCallback(
      (tab: SectionId) => {
        dispatch({ type: 'SET_ACTIVE_TAB', payload: { tab } });
      },
      [dispatch]
    );

    const setSearchMode = useCallback(
      (searchMode: SearchMode) => {
        dispatch({ type: 'SET_SEARCH_MODE', payload: { searchMode } });
      },
      [dispatch]
    );

    const {
      activeTab,
      filters,
      focusedKey,
      focusedKeyByTab,
      isModalOpen,
      query,
      scopedToProject,
      scopedToProjectId,
      searchMode,
      selectedProject,
    } = state;

    const appliedFilters = useMemo(
      () => getQuickSearchFilters(filters),
      [filters]
    );

    const requestVariables = useMemo(() => {
      // When scoped to a project, the 'projects' filter doesn't apply —
      // there are no sub-projects inside a project. Strip it before sending
      // so it doesn't block results while still preserving the filter state.
      const scopedFilters = scopedToProjectId
        ? getQuickSearchFilters({ ...filters, projects: false })
        : appliedFilters;

      const baseVars: GetQuickSearchResultsV2QueryVariables = {
        accountId,
        page: {
          first: SEARCH_RESULTS_PAGE_COUNT,
        },
        query,
        filters: scopedFilters,
      };

      if (scopedToProjectId) {
        return {
          ...baseVars,
          scope: { projectId: scopedToProjectId },
        };
      }

      return baseVars;
    }, [accountId, query, appliedFilters, filters, scopedToProjectId]);

    const value = useMemo(
      () => ({
        activeTab,
        clearAllFilters,
        clearQuery,
        clearScopedToProject,
        clearScopedToProjectId,
        clearSelectedProject,
        closeModal,
        downloadAssets,
        filters,
        focusedKey,
        focusedKeyByTab,
        isAssetDownloading: isDownloading,
        isModalOpen,
        openModal: handleOpenModal,
        query,
        requestVariables,
        scopedToProject,
        scopedToProjectId,
        searchMode,
        selectedProject,
        setActiveTab,
        setFocusedKey,
        setQuery,
        setScopedToProject,
        setScopedToProjectId,
        setSearchMode,
        setSelectedProject,
        toggleFilter,
      }),
      [
        activeTab,
        clearAllFilters,
        clearQuery,
        clearScopedToProject,
        clearScopedToProjectId,
        clearSelectedProject,
        closeModal,
        downloadAssets,
        filters,
        focusedKey,
        focusedKeyByTab,
        handleOpenModal,
        isDownloading,
        isModalOpen,
        query,
        requestVariables,
        scopedToProject,
        scopedToProjectId,
        searchMode,
        selectedProject,
        setActiveTab,
        setFocusedKey,
        setQuery,
        setScopedToProject,
        setScopedToProjectId,
        setSearchMode,
        setSelectedProject,
        toggleFilter,
      ]
    );

    return (
      <QuickSearchStateContext.Provider value={value}>
        <KeyboardShortcut
          id="global.quickSearch"
          onKeyDown={() => {
            trackSearchOpened(getSearchOpenedSource(pathname, 'keyboard'));
            value.openModal();
          }}
        />
        {children}
      </QuickSearchStateContext.Provider>
    );
  }
);

export type UseQuickSearchModalResults = {
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: (options?: CloseModalOptions) => void;
};

export const useQuickSearchModal = () => {
  return useContextSelector(
    QuickSearchStateContext,
    ({
      clearAllFilters,
      clearQuery,
      clearScopedToProject,
      clearScopedToProjectId,
      clearSelectedProject,
      closeModal,
      filters,
      focusedKey,
      isModalOpen,
      openModal,
      query,
      requestVariables,
      scopedToProject,
      scopedToProjectId,
      selectedProject,
      setFocusedKey,
      setQuery,
      setScopedToProject,
      setScopedToProjectId,
      setSelectedProject,
      toggleFilter,
    }) => ({
      clearAllFilters,
      clearQuery,
      clearScopedToProject,
      clearScopedToProjectId,
      clearSelectedProject,
      closeModal,
      filters,
      focusedKey,
      isModalOpen,
      openModal,
      query,
      requestVariables,
      scopedToProject,
      scopedToProjectId,
      selectedProject,
      setFocusedKey,
      setQuery,
      setScopedToProject,
      setScopedToProjectId,
      setSelectedProject,
      toggleFilter,
    })
  );
};

export const useQuickSearchActiveTab = () => {
  const activeTab = useContextSelector(
    QuickSearchStateContext,
    (value) => value?.activeTab
  );

  const setActiveTab = useContextSelector(
    QuickSearchStateContext,
    (value) => value?.setActiveTab
  );

  return { activeTab, setActiveTab };
};

export const useQuickSearchSearchMode = () => {
  const searchMode = useContextSelector(
    QuickSearchStateContext,
    (value) => value?.searchMode
  );

  const setSearchMode = useContextSelector(
    QuickSearchStateContext,
    (value) => value?.setSearchMode
  );

  return { searchMode, setSearchMode };
};

export const useScopedToProjectId = () => {
  return useContextSelector(
    QuickSearchStateContext,
    (value) => value?.scopedToProjectId
  );
};

export const useQuickSearchDownloadAssets = () => {
  const downloadAssets = useContextSelector(
    QuickSearchStateContext,
    (value) => value?.downloadAssets
  );

  const isAssetDownloading = useContextSelector(
    QuickSearchStateContext,
    (value) => value?.isAssetDownloading
  );

  return {
    downloadAssets,
    isAssetDownloading,
  };
};
